"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BookParser = void 0;
const format_string_1 = require("common/format-string");
const KeyClassifier_1 = require("./KeyClassifier");
const BookParserError_1 = require("./BookParserError");
class BookParser {
    constructor(parseOption) {
        this.parseOption = parseOption;
        this.keyClassifier = new KeyClassifier_1.KeyClassifier({
            originalScriptField: parseOption.originalScriptField,
            translatedScriptFieldFormat: parseOption.translatedScriptFieldFormat,
            langAliases: parseOption.langAliases,
        });
    }
    parse(book) {
        var _a, _b, _c, _d, _e, _f;
        const scripts = [];
        const errors = [];
        // parse rows
        for (let sheetNo = 0; sheetNo < book.sheets.length; sheetNo++) {
            const sheet = book.sheets[sheetNo];
            const rows = sheet.rows;
            const header = rows[(_a = this.parseOption.ignoreLines) !== null && _a !== void 0 ? _a : 0];
            const columnKeys = this.parseHeader(header.columns);
            const textColumn = columnKeys.find((key) => key.type === "text");
            if (!textColumn) {
                errors.push({
                    type: "TextColumnNotFound",
                    sheetName: sheet.name,
                });
                continue;
            }
            for (let rowNo = ((_b = this.parseOption.ignoreLines) !== null && _b !== void 0 ? _b : 0) + 1; rowNo < rows.length; rowNo++) {
                const row = rows[rowNo];
                let text = "";
                const translations = {};
                const custom = {};
                const additionalData = {};
                const additionalDataOptional = {};
                const translationIgnore = {};
                // Derived Values (initial)
                const dataRowNo = rowNo - ((_c = this.parseOption.ignoreLines) !== null && _c !== void 0 ? _c : 0);
                const padRowNo = dataRowNo.toString().padStart(5, "0");
                custom["row_no"] = padRowNo;
                custom["sheet_name"] = sheet.name;
                custom["sheetName"] = sheet.name;
                // Raw Values
                columnKeys.forEach((key) => {
                    var _a, _b, _c;
                    if (key.type === "text") {
                        text = (_a = row.columns[key.index]) !== null && _a !== void 0 ? _a : "";
                        return;
                    }
                    if (key.type === "translation") {
                        translations[key.langId] = (_b = row.columns[key.index]) !== null && _b !== void 0 ? _b : "";
                        if (this.parseOption.uploadMode === "reference") {
                            translationIgnore[key.langId] = !translations[key.langId];
                        }
                        return;
                    }
                    if (key.type === "custom") {
                        custom[key.key] = (_c = row.columns[key.index]) !== null && _c !== void 0 ? _c : "";
                        return;
                    }
                });
                if (!text)
                    continue;
                // Derived Values
                let scriptId = "";
                // console.log("scriptIdFormat", this.parseOption.scriptIdFormat);
                if (this.parseOption.scriptIdFormat) {
                    scriptId = (0, format_string_1.formatMustache)(this.parseOption.scriptIdFormat, Object.assign(Object.assign({}, custom), translations));
                }
                else {
                    const padSheetNo = (sheetNo + 1).toString().padStart(2, "0");
                    scriptId = custom["id"] || `${padSheetNo}-${padRowNo}`;
                }
                // console.log("scriptId", scriptId);
                if (!scriptId) {
                    errors.push({
                        type: "EmptyID",
                        sheetName: sheet.name,
                        rowIndex: rowNo,
                    });
                    continue;
                }
                const bannedCharacters = [".", "[", "]", "*", "`"];
                if (bannedCharacters.some((char) => scriptId.includes(char))) {
                    errors.push({
                        type: "ProhibitedCharacterInID",
                        sheetName: sheet.name,
                        rowIndex: rowNo,
                        invalidId: scriptId,
                        prohibitedCharacters: bannedCharacters,
                    });
                }
                let scriptType = "";
                if (this.parseOption.scriptTypeFormat) {
                    scriptType = (0, format_string_1.formatMustache)(this.parseOption.scriptTypeFormat, custom);
                }
                else {
                    scriptType =
                        (custom["foreign_table"] || "") +
                            "_" +
                            (custom["foreign_column"] || "");
                }
                const shouldIncludedIntoSegmentLevelWordTable = ((_d = custom["wordTable"]) !== null && _d !== void 0 ? _d : "") !== "";
                for (const columnToSave of (_e = this.parseOption.columnsToSave) !== null && _e !== void 0 ? _e : []) {
                    if (custom[columnToSave])
                        additionalData[columnToSave] = custom[columnToSave];
                }
                for (const col of (_f = this.parseOption.columnsToSaveOptional) !== null && _f !== void 0 ? _f : []) {
                    if (custom[col])
                        additionalDataOptional[col] = custom[col];
                }
                scripts.push({
                    scriptId,
                    scriptType,
                    shouldIncludedIntoSegmentLevelWordTable,
                    text,
                    translations,
                    additionalData,
                    additionalDataOptional,
                    translationIgnore,
                    custom,
                    rawSheetName: sheet.name,
                    rawRowIndex: rowNo,
                    rawTextColumnIndex: textColumn.index,
                    rawTranslationColumnIndices: Object.assign({}, columnKeys.reduce((acc, key) => {
                        if (key.type !== "translation")
                            return acc;
                        return Object.assign(Object.assign({}, acc), { [key.langId]: key.index });
                    }, {})),
                });
            }
        }
        // check duplicated id
        const scriptIdSet = new Set();
        const duplicatedScriptIdset = new Set();
        scripts.forEach((script) => {
            if (!scriptIdSet.has(script.scriptId)) {
                scriptIdSet.add(script.scriptId);
                return;
            }
            if (!duplicatedScriptIdset.has(script.scriptId)) {
                duplicatedScriptIdset.add(script.scriptId);
                errors.push({
                    type: "DuplicatedID",
                    rows: scripts
                        .filter((s) => s.scriptId === script.scriptId)
                        .map((s) => ({
                        sheetName: s.rawSheetName,
                        rowIndex: s.rawRowIndex,
                    })),
                });
            }
        });
        if (errors.length > 0) {
            throw new BookParserError_1.BookParserError(errors);
        }
        return scripts;
    }
    parseHeader(header) {
        return header
            .map((column, colIndex) => (Object.assign(Object.assign({}, this.keyClassifier.classify(column)), { key: column, index: colIndex })))
            .filter(({ key }) => !!key);
    }
}
exports.BookParser = BookParser;
